import { useEffect, useState, useContext } from "react";
import {
  UserIcon,
  UsersIcon,
  HomeModernIcon,
} from "@heroicons/react/24/outline";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import { Badge } from "../ui/Badge";
import { cabinsApi } from "../../api/cabins";
import { adminApi } from "../../api/admin";
import { campersApi } from "../../api/campers";
import { AuthContext } from "../../context/AuthContext";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import type { Cabin, CabinGroup, User, Camper } from "../../types";

interface EnrollmentEntry {
  id: number;
  camperId: number;
  year: number;
  cabinId?: number | null;
  overrideCabinId?: number | null;
  camper?: Camper;
  /** Sync-assigned cabin (the row before any admin override). */
  cabin?: { id: number; name: string } | null;
}

interface CounselorAssignment {
  id: number;
  cabinId: number;
  userId: number;
  termId: number | null;
  overrideCabinId: number | null;
  overrideCabin?: { id: number; name: string } | null;
}

interface CabinDetail extends Cabin {
  enrollments?: EnrollmentEntry[];
  counselorAssignments?: CounselorAssignment[];
}

interface Props {
  cabin: Cabin;
  cabinGroups: CabinGroup[];
  year: number;
  termId?: number;
  termName?: string;
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

type Tab = "details" | "counselors" | "campers";

const cabinSchema = z.object({
  name: z.string().min(1, "Name is required"),
  cabinGroupId: z.coerce
    .number()
    .optional()
    .transform((v) => (v && v > 0 ? v : null)),
});

type CabinFormData = z.infer<typeof cabinSchema>;

export function CabinDetailModal({ cabin, cabinGroups, year, termId, termName, open, onClose, onUpdated }: Props) {
  const { user: currentUser } = useContext(AuthContext);
  const canEdit = currentUser?.role === "admin" || currentUser?.role === "director";

  const [activeTab, setActiveTab] = useState<Tab>("details");
  const [detail, setDetail] = useState<CabinDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Counselor assignment state
  const [allUsers, setAllUsers] = useState<User[]>([]);
  // All cabins (used to populate the per-counselor override picker)
  const [allCabins, setAllCabins] = useState<Cabin[]>([]);

  // Camper assignment state
  const [allCampers, setAllCampers] = useState<Camper[]>([]);

  // Details form
  const form = useForm<CabinFormData>({
    resolver: zodResolver(cabinSchema),
  });
  const [saving, setSaving] = useState(false);

  const reload = () => {
    setLoadingDetail(true);
    cabinsApi
      .get(cabin.id, termId !== undefined ? { termId } : { year })
      .then((d) => setDetail(d as CabinDetail))
      .catch(() => toast.error("Failed to load cabin details"))
      .finally(() => setLoadingDetail(false));
  };

  useEffect(() => {
    if (!open) return;
    setActiveTab("details");
    setDetail(null);
    reload();
    if (canEdit) {
      adminApi.listUsers().then(setAllUsers).catch(() => toast.error("Failed to load users"));
      campersApi.list({ pageSize: 500 }).then((r) => setAllCampers(r.data)).catch(() => toast.error("Failed to load campers"));
      cabinsApi.list({ includeArchived: false }).then(setAllCabins).catch(() => toast.error("Failed to load cabins"));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cabin.id]);

  useEffect(() => {
    if (detail) {
      form.reset({
        name: detail.name,
        cabinGroupId: detail.cabinGroupId ?? 0,
      });
    }
  }, [detail, form]);

  const onSaveDetails = async (data: CabinFormData) => {
    setSaving(true);
    try {
      await cabinsApi.update(cabin.id, { name: data.name, cabinGroupId: data.cabinGroupId as number | null });
      toast.success("Cabin updated!");
      onUpdated();
      reload();
    } catch {
      toast.error("Failed to update cabin");
    } finally {
      setSaving(false);
    }
  };

  const counselors: User[] = detail?.counselors ?? [];
  const counselorAssignments: CounselorAssignment[] = detail?.counselorAssignments ?? [];
  const enrolledCampers: EnrollmentEntry[] = detail?.enrollments ?? [];

  const handleOverrideCounselorCabin = async (userId: number, value: string) => {
    const overrideCabinId = value === "" ? null : Number(value);
    try {
      await cabinsApi.overrideCounselorCabin(cabin.id, userId, overrideCabinId, termId);
      toast.success(overrideCabinId ? "Override set" : "Override cleared");
      onUpdated();
      reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update override");
    }
  };

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: "details", label: "Details" },
    { id: "counselors", label: "Counselors", count: counselors.length },
    { id: "campers", label: "Campers", count: enrolledCampers.length },
  ];

  return (
    <Modal open={open} onClose={onClose} title={cabin.name} size="lg">
      {/* Tab bar */}
      <div className="-mx-6 -mt-5 px-6 border-b border-stone-100 mb-5">
        <div className="flex gap-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-forest-600 text-forest-700"
                  : "border-transparent text-stone-500 hover:text-stone-700"
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-1.5 text-xs text-stone-400">({tab.count})</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {loadingDetail ? (
        <div className="space-y-3 animate-pulse">
          <div className="h-4 bg-stone-100 rounded w-2/3" />
          <div className="h-4 bg-stone-100 rounded w-1/2" />
          <div className="h-4 bg-stone-100 rounded w-3/4" />
        </div>
      ) : (
        <>
          {/* ─── Details Tab ─────────────────────────────────────── */}
          {activeTab === "details" && (
            <form onSubmit={form.handleSubmit(onSaveDetails)} className="space-y-4">
              <Input
                label="Cabin Name"
                disabled={!canEdit}
                error={form.formState.errors.name?.message}
                {...form.register("name")}
              />
              <Select
                label="Cabin Group (optional)"
                disabled={!canEdit}
                options={[
                  { value: 0, label: "Unassigned" },
                  ...cabinGroups.filter((cg) => !cg.archivedAt).map((cg) => ({ value: cg.id, label: cg.name })),
                ]}
                error={form.formState.errors.cabinGroupId?.message}
                {...form.register("cabinGroupId")}
              />
              {canEdit && (
                <div className="flex justify-end pt-2">
                  <Button type="submit" variant="primary" loading={saving}>
                    Save Changes
                  </Button>
                </div>
              )}
            </form>
          )}

          {/* ─── Counselors Tab ───────────────────────────────────── */}
          {activeTab === "counselors" && (
            <div className="space-y-4">

              {/* Counselor list */}
              {counselors.length === 0 ? (
                <div className="text-center py-8">
                  <UserIcon className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                  <p className="text-sm text-stone-400">No counselors assigned</p>
                </div>
              ) : (
                <ul className="divide-y divide-stone-100">
                  {counselors.map((c) => {
                    // Find this counselor's assignment row for the active term
                    // (or the term-less row if no termId is in scope) so we
                    // know whether an override is currently in place.
                    const assignment = counselorAssignments.find(
                      (a) => a.userId === c.id && (termId === undefined || a.termId === termId || a.termId === null)
                    );
                    const overrideValue = assignment?.overrideCabinId ?? "";
                    return (
                      <li key={c.id} className="flex items-center justify-between py-2.5 gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-forest-100 flex items-center justify-center text-forest-700 text-xs font-semibold shrink-0">
                            {c.firstName[0]}{c.lastName[0]}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-stone-800 truncate">
                              {c.firstName} {c.lastName}
                            </p>
                            <p className="text-xs text-stone-400 truncate">{c.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge label={c.role} color="stone" />
                          {canEdit && assignment && (
                            <select
                              value={overrideValue}
                              onChange={(e) => handleOverrideCounselorCabin(c.id, e.target.value)}
                              className="text-xs rounded border border-stone-300 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-forest-500"
                              title="Override cabin for this counselor"
                            >
                              <option value="">Default cabin</option>
                              {allCabins
                                .filter((opt) => opt.id !== cabin.id)
                                .map((opt) => (
                                  <option key={opt.id} value={opt.id}>
                                    Override → {opt.name}
                                  </option>
                                ))}
                            </select>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* ─── Campers Tab ──────────────────────────────────────── */}
          {activeTab === "campers" && (
            <div className="space-y-4">
              <p className="text-xs text-stone-400">Showing enrollments for {termName ?? year}</p>

              {/* Camper list */}
              {enrolledCampers.length === 0 ? (
                <div className="text-center py-8">
                  <UsersIcon className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                  <p className="text-sm text-stone-400">No campers assigned for {year}</p>
                </div>
              ) : (
                <ul className="divide-y divide-stone-100">
                  {enrolledCampers.map((e) => {
                    const c = e.camper;
                    if (!c) return null;
                    // A camper appears on this card via override when their
                    // overrideCabinId matches but their original cabin (cabinId)
                    // does not — i.e. they were moved here from somewhere else.
                    // Counselors don't see this hint; the surrounding canEdit
                    // gate restricts the modal's interactive surfaces and we
                    // mirror that here for the audit string.
                    const movedFrom =
                      canEdit &&
                      e.overrideCabinId === cabin.id &&
                      e.cabin &&
                      e.cabin.id !== cabin.id
                        ? e.cabin.name
                        : null;
                    return (
                      <li key={e.id} className="flex items-center py-2.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-bark-100 flex items-center justify-center text-bark-700 text-xs font-semibold">
                            {c.firstName[0]}{c.lastName[0]}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-stone-800">
                              {c.firstName} {c.lastName}
                            </p>
                            {c.team === "army" && <Badge label="A" color="army" />}
                            {c.team === "navy" && <Badge label="N" color="navy" />}
                            {movedFrom && (
                              <span
                                className="text-xs text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded"
                                title="Admin override — sync-assigned cabin shown"
                              >
                                Moved from cabin {movedFrom}
                              </span>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      {/* Footer info */}
      <div className="mt-5 -mx-6 -mb-5 px-6 py-3 bg-stone-50 border-t border-stone-100 flex items-center gap-2">
        <HomeModernIcon className="w-4 h-4 text-stone-400" />
        <span className="text-xs text-stone-400">
          {detail?.cabinGroup?.division?.name ?? detail?.cabinGroup?.name ?? "Unassigned"} · {enrolledCampers.length} camper{enrolledCampers.length !== 1 ? "s" : ""} · {counselors.length} counselor{counselors.length !== 1 ? "s" : ""}
        </span>
      </div>
    </Modal>
  );
}
